import axios from '@/api/config/axios';

export const PLAN_OVERAGE_CONSENT_REQUIRED = 'PLAN_OVERAGE_CONSENT_REQUIRED';

/**
 * Extrai payload de excedente quando a API devolve 422 com consentimento obrigatório.
 * @param {import('axios').AxiosError} error
 * @returns {Record<string, unknown>|null}
 */
export function extractPlanOverageConsent422(error) {
    if (!error?.response || error.response.status !== 422) {
        return null;
    }
    const body = error.response.data || {};
    const data = body.data ?? body.error_data ?? null;
    if (!data || data.error_code !== PLAN_OVERAGE_CONSENT_REQUIRED) {
        return null;
    }
    return data;
}

/**
 * Exibe diálogo e registra aceite no backend.
 * @param {Record<string, unknown>} overageData
 * @returns {Promise<boolean>} true se o aceite foi enviado (novo ou já existente)
 */
/**
 * Executa um pedido HTTP; se a API exigir aceite de excedente para o recurso esperado, pede confirmação e repete uma vez.
 * @param {() => Promise<import('axios').AxiosResponse>} exec
 * @param {'envios_redacoes'|'correcoes_ia'|'revisoes_texto_humano'} expectedResource
 */
export async function executeWithPlanOverageConsentRetry(exec, expectedResource) {
    try {
        return await exec();
    } catch (error) {
        const over = extractPlanOverageConsent422(error);
        if (over?.recurso === expectedResource) {
            const ok = await promptAndAcceptPlanOverageConsent(over);
            if (ok) {
                return await exec();
            }
        }
        throw error;
    }
}

export async function promptAndAcceptPlanOverageConsent(overageData) {
    const Swal = window.Swal;
    if (!Swal) {
        throw new Error('Interface de confirmação indisponível.');
    }

    const price = Number(overageData.overage_unit_price || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const recursoLabel =
        overageData.recurso === 'envios_redacoes'
            ? 'documentos corrigidos'
            : overageData.recurso === 'correcoes_ia'
              ? 'usos de IA'
              : overageData.recurso === 'revisoes_texto_humano'
                ? 'revisões de texto humano'
                : 'este recurso';

    const html = `
        <p>O limite incluído do seu plano para <strong>${recursoLabel}</strong> foi atingido neste ciclo.</p>
        <p>Cada unidade adicional será cobrada em <strong>R$ ${price}</strong>.</p>
        <p>Ao confirmar, o aceite fica registrado e novos usos passam a gerar débito de excedente até a renovação do ciclo.</p>
        <p>Um boleto a parte será gerado para o pagamento do excedente ao final do ciclo.</p>
    `;

    const result = await Swal.fire({
        icon: 'warning',
        title: 'Continuar com uso excedente?',
        html,
        showCancelButton: true,
        focusCancel: true,
        confirmButtonText: 'Aceito e desejo continuar',
        cancelButtonText: 'Não aceito',
        width: '34rem',
    });

    if (!result.isConfirmed) {
        return false;
    }

    await axios.post('/api/v1/plan-overage/accept', {
        resource: overageData.recurso,
        terms_version: overageData.terms_version,
        organization_id: overageData.organization_id || null,
    });

    return true;
}
